import { useSyncExternalStore } from 'react';

/**
 * What a finger needs that a mouse and a keyboard already have (the responsive pass, 2026-10-26).
 *
 * Shift and Option change what a press does — Shift adds to the selection and keeps proportions,
 * Option drags a copy — and a touch screen has neither key. The two chips by the zoom pill hold
 * them down instead, until they are pressed again; every press on the canvas reads them as if the
 * keys were held, so the tools need no knowledge of them.
 */
export interface HeldKeys {
  readonly shift: boolean;
  readonly alt: boolean;
}

let held: HeldKeys = { shift: false, alt: false };
const listeners = new Set<() => void>();

export function heldKeys(): HeldKeys {
  return held;
}

export function setHeldKey(key: keyof HeldKeys, on: boolean): void {
  if (held[key] === on) return;
  held = { ...held, [key]: on };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useHeldKeys(): HeldKeys {
  return useSyncExternalStore(subscribe, heldKeys, heldKeys);
}

/**
 * The event as the tools should read it: with the held chips counted as keys. The event itself is
 * left alone — a DOM event's keys cannot be set — and only when a chip is on is it wrapped.
 */
export function withHeldKeys<E extends MouseEvent>(event: E): E {
  const { shift, alt } = held;
  if ((!shift || event.shiftKey) && (!alt || event.altKey)) return event;
  return new Proxy(event, {
    get(target, key) {
      if (key === 'shiftKey') return target.shiftKey || shift;
      if (key === 'altKey') return target.altKey || alt;
      const value: unknown = Reflect.get(target, key, target);
      // An event's methods check what they are called on: called through the wrapper, they throw.
      return typeof value === 'function' ? (value as (...args: unknown[]) => unknown).bind(target) : value;
    },
  });
}

/**
 * How far from a handle a press still takes it. A fingertip covers ten times what a pointer's tip
 * does, and the laptop's 7 px reach made a corner handle all but impossible to land on, so a press
 * made with a finger reaches two and a half times as far. A pen is as precise as a mouse.
 */
export const TOUCH_REACH = 2.5;
let touching = false;

/** Called with every press and move over the canvas: whether the hand on it is a finger. */
export function noteTouch(event: PointerEvent): void {
  touching = event.pointerType === 'touch';
}

/**
 * Any press anywhere says which hand is in use, not only the canvas's own: on a laptop with a touch
 * screen, a mouse drag out of Assets (a hit test with no canvas press before it) would otherwise keep
 * the finger's wide reach from the last touch.
 */
if (typeof window !== 'undefined') window.addEventListener('pointerdown', noteTouch, true);

export function reach(px: number): number {
  return touching ? px * TOUCH_REACH : px;
}
